import type { FC } from "react";
import React from "react";

import cn from "classnames";

import { IconSizeErrorBoundary } from "../../../../HOCs/IconSizeErrorBoundary";
import { Icon } from "../../../Icon";
import { ICON_SIZE_BY_AVA_PICTURE_SIZE_MAP } from "../../constants/sizes";
import { createDisplayNameForAvaPictureElement } from "../../utils/getDisplayName";
import { createTestIdForAvaPictureElement } from "../../utils/getTestId";

import type {
    OmitOfUnion,
    PropsWithCustomVarsStyles,
} from "../../../../types/react";
import type { IconProps } from "../../../Icon";
import type { AvaPictureShapes, AvaPictureSizes } from "../../types";

import classes from "./AvaPictureIcon.styl";

type AvaPictureIconPrivateProps = PropsWithCustomVarsStyles<
    OmitOfUnion<IconProps, "size" | "className">
> & {
    size: AvaPictureSizes;
    shape: AvaPictureShapes;
};

export type AvaPictureIconProps = Omit<
    AvaPictureIconPrivateProps,
    "size" | "shape"
>;

const AVA_PICTURE_ICON_DISPLAY_NAME =
    createDisplayNameForAvaPictureElement("Icon");
export const AVA_PICTURE_ICON_TEST_ID =
    createTestIdForAvaPictureElement("icon");

export const AvaPictureIcon: FC<AvaPictureIconProps> = (props) => {
    const { icon, size, shape, customVarsStyles } =
        props as AvaPictureIconPrivateProps;

    const iconSize = ICON_SIZE_BY_AVA_PICTURE_SIZE_MAP[size];

    const iconProps = {
        size: iconSize,
        icon,
        className: cn(classes["icon"], customVarsStyles),
    } as IconProps;

    const classNames = cn(
        classes["icon-container"],
        classes[`__size-${size}`],
        classes[`__shape-${shape}`],
        customVarsStyles,
    );

    return (
        <div data-tsid={AVA_PICTURE_ICON_TEST_ID} className={classNames}>
            <IconSizeErrorBoundary
                componentName={AVA_PICTURE_ICON_DISPLAY_NAME}
                componentSize={size}
                iconName={icon}
                iconSize={iconSize}
            >
                <Icon {...iconProps} />
            </IconSizeErrorBoundary>
        </div>
    );
};

AvaPictureIcon.displayName = AVA_PICTURE_ICON_DISPLAY_NAME;

export default AvaPictureIcon;
